import { DateField as FieldDefinition } from '@bybeam/platform-types';
import DateInput from '@platform-ui-common/components/DateInput/index.js';
import {
  GenericFormData,
  fieldUpdate,
  updateRepeated,
} from '@platform-ui-common/hooks/useForm/index.js';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FieldProps } from './types.js';
import { DateValidationError } from '@mui/lab/internal/pickers/date-utils.js';
import { useState, useMemo, useEffect } from 'react';
dayjs.extend(customParseFormat);

export default function DateField<FormData extends GenericFormData>({
  field: { key, copy, props, validation: configValidation },
  validation,
  answers,
  dispatch,
  repeated,
  errors,
  setValidationOverrides,
  ...rest
}: FieldProps<FieldDefinition, FormData>): JSX.Element {
  const [error, setError] = useState<DateValidationError | null>(null);
  const minimumDate = dayjs(configValidation?.minimumDate, 'MM-DD-YYYY');
  const maximumDate = dayjs(configValidation?.maximumDate, 'MM-DD-YYYY');
  const id = `${key}${repeated?.index ? `-${repeated?.index}` : ''}`;

  const errorMessage = useMemo(() => {
    if (answers[key]) {
      switch (error) {
        case 'maxDate': {
          return maximumDate.isValid()
            ? `Invalid Date. Must be on or before ${maximumDate.format('MM/DD/YYYY')}`
            : 'Your date is not valid';
        }
        case 'minDate': {
          return minimumDate.isValid()
            ? `Invalid Date. Must be on or after ${minimumDate.format('MM/DD/YYYY')}`
            : 'Your date is not valid';
        }

        case 'invalidDate': {
          return 'Your date is not valid';
        }

        default: {
          return;
        }
      }
    }
  }, [error, minimumDate, maximumDate, answers[key], key]);

  useEffect(() => {
    if (answers[key]) {
      setValidationOverrides?.((overrides) => ({ ...overrides, [id]: !!errorMessage }));
    }

    return () => setValidationOverrides?.((overrides) => ({ ...overrides, [id]: false }));
  }, [errorMessage, setValidationOverrides, answers[key], key, id]);

  const getMinDate = () => {
    if (configValidation?.allowedRange === 'today') return dayjs();
    return minimumDate.isValid() ? minimumDate : undefined;
  };

  const getMaxDate = () => {
    if (configValidation?.allowedRange === 'today') return dayjs();
    return maximumDate.isValid() ? maximumDate : undefined;
  };

  const getHelperText = () => {
    if (configValidation?.allowedRange === 'today') return dayjs().format('MM/DD/YYYY');
    return errorMessage ? errorMessage : props?.helperText;
  };

  return (
    <div className={'flex flex-col lg:max-w-[36rem]'}>
      <DateInput
        id={id}
        label={copy}
        value={answers[key] ?? ''}
        onChange={(value): void =>
          repeated
            ? dispatch(updateRepeated(repeated.parentKey, repeated.index, key, value))
            : dispatch(fieldUpdate(key, value as FormData[typeof key]))
        }
        required={validation?.required}
        minDate={getMinDate()}
        maxDate={getMaxDate()}
        onError={(newError) => setError(newError)}
        error={(errors[key] as string) || errorMessage}
        {...props}
        {...rest}
        helperText={getHelperText()}
      />
    </div>
  );
}
